// ─────────────────────────────────────────────────────────────
// CATECHIST READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// Same structure as Martial Arts Kids Class Ready (the test that sold), with a NEW palette (liturgical green sections,
// Marian blue buttons, gold and Advent purple accents), NEW button copy and a NEW section for DREs who buy for the team.
// Buyers (22/10): Catholic school religion teachers first (the buyer EduLabs targets with Religione Pronta), parish
// catechists and the DREs who lead them.
// Promise (must match ads, checkout and the PDF): 32-week year plan (September to May, liturgical year) +
// 10 complete 60-minute First Communion sessions (ages 7–10), $27 one-time, 10-day guarantee, the 5 bonuses in checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Catholic content: faithful to Church teaching, no named publisher, diocese or program, no testimonials.
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://catechist-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'Catechist Ready™ | Your Whole Year of Religion Classes, Already Planned';
export const META_DESCRIPTION =
  'For Catholic school religion teachers and parish catechists: a 32-week plan that follows the liturgical year, plus 10 complete, ready-to-teach First Communion sessions.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'CATECHIST READY™';
/** Label for every button that scrolls to the offer section (new copy for this landing) */
export const ctaLabel = 'GET MY WHOLE YEAR READY';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'MY WHOLE YEAR · $27';
export const topBarMobileLabel = 'GET IT · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR RELIGION TEACHERS, CATECHISTS & DREs',
  titleLead: 'YOUR WHOLE YEAR OF RELIGION CLASSES,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 32-week plan that follows the liturgical year, plus 10 complete First Communion sessions, ready to teach in your religion class or parish group.',
  mockupAlt: 'Catechist Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '32', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE SESSIONS' },
  { value: '60', label: 'MINUTES, FULLY TIMED' },
  { value: '7–10', label: 'YEARS OLD · FIRST COMMUNION' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'THE NIGHT BEFORE VS. CLASS TIME.',
  before: {
    label: 'BEFORE',
    when: 'The night before',
    quote: '“What do I teach the kids tomorrow?”',
    items: ['Searching ideas on Pinterest.', 'A theory book that doesn’t say what to do.', 'The same coloring page again.'],
  },
  after: {
    label: 'AFTER',
    when: 'Class time',
    items: ['Open this week’s page.', 'Prepare in 10 minutes.', 'Pray, tell the story, talk and do the activity.', 'Close with prayer and send the parent note.'],
  },
};

// ── 3. WHAT DOES YOUR GROUP NEED THIS WEEK (pick a need, see the session that is ready for it) ──
const block = 'Session block: welcome & prayer 8 · Bible story 12 · faith talk 10 · activity 15 · worksheet 10 · closing prayer 5';
export const selector = {
  eyebrow: 'START WHERE YOUR CLASS IS',
  title: 'WHAT DOES YOUR CLASS NEED THIS WEEK?',
  sub: 'Pick a need and see the session that is ready for it.',
  startLabel: 'YOUR NEXT SESSION',
  tablistLabel: 'Session needs',
  columns: ['PAGE 1 · SESSION PLAN', 'PAGE 2 · TELL THE STORY', 'PAGE 3 · ACTIVITY & PRAYER'],
  note: 'All 10 sessions follow this same 3-page structure. Use it with your school or parish program.',
  items: [
    {
      tab: 'A warm first week',
      n: 'SESSION 01 · WEEK 1',
      title: 'We Are God’s Family',
      goal: 'The child says their name to the group, makes the Sign of the Cross and knows that in Baptism we become children of God and part of the Church.',
      open: ['Session goal, supplies and 10-minute prep', 'Your opening words: “Each one of you is here because God knows your name and loves you.”', block],
      activity: ['The story in kids’ words in 5 steps, with the Bible passage', 'Key truth, 3 faith talk questions and an age level card', 'A tricky question kids really ask, with a simple answer'],
      close: ['Activity: Name Circle Blessing, with a room diagram', 'Worksheet: My Baptism', 'Closing prayer, take-home verse and parent note'],
    },
    {
      tab: 'Advent is coming',
      n: 'SESSION 05 · WEEK 12',
      title: 'Advent: Getting Ready for Jesus',
      goal: 'The child explains that Advent is the time of getting our hearts ready for Jesus, and knows the Advent wreath: 4 candles for 4 Sundays.',
      open: ['Session goal, supplies and 10-minute prep', 'Your opening words: “What do we do when a very special guest is coming? We get ready!”', block],
      activity: ['The story of the long wait for the Savior, in 5 steps', 'Why the third candle is rose, explained for kids', 'Age level card for ages 7–8 and 9–10'],
      close: ['Activity: Advent Kindness Chain', 'Worksheet: My Advent Wreath', 'Closing prayer, take-home verse and parent note'],
    },
    {
      tab: 'First Reconciliation is near',
      n: 'SESSION 09 · WEEK 23',
      title: 'Jesus Forgives: The Lost Sheep',
      goal: 'The child tells the story of the lost sheep and knows that God always forgives us when we are sorry, especially in the Sacrament of Reconciliation.',
      open: ['Session goal, supplies and 10-minute prep', 'Your opening words: “Have you ever lost something special?”', block],
      activity: ['The parable in 5 story steps, with the Bible passage', 'The steps of Reconciliation, explained with joy, not fear', 'A tricky question: “Will the priest tell anyone my sins?”'],
      close: ['Activity: Find the Lost Sheep', 'Worksheet: The Good Shepherd', 'Closing prayer, take-home verse and parent note'],
    },
    {
      tab: 'Ready for First Communion',
      n: 'SESSION 10 · WEEK 27',
      title: 'The Mass: Our Great Celebration',
      goal: 'The child tells the story of the Last Supper, names the four parts of the Mass and practices how to receive Holy Communion with reverence.',
      open: ['Session goal, supplies and 10-minute prep', 'Your opening words: “At the Last Supper, Jesus gave us the greatest gift: himself.”', 'Its own 60-minute session block, the most complete of the book'],
      activity: ['The Last Supper in 5 story steps', 'The 4 parts of the Mass, with icons', 'A tricky question: “It still looks like bread. How is it Jesus?”'],
      close: ['Communion practice in 6 steps', 'The Eucharistic fast and a First Communion countdown for families', 'Closing prayer, blessing and parent note'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE IN YOUR HANDS FOR YOUR NEXT CLASS.',
  sub: 'Open any page and read it at full size: your year plan, a complete session in 3 pages and your session planner.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: [
    'Your 32-week plan (fall)',
    'Session 02 · session plan',
    'Session 02 · tell the story',
    'Session 02 · activity & prayer',
    'Session 10 · Communion practice',
    'Session planner + attendance',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY SESSION',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST PRAY AND TEACH.',
  text: 'Every week from September to May is mapped, following the liturgical year: the start of one of the 10 complete sessions, a ready theme with its Bible passage and a 10-minute activity, or a review and celebrate week. Each session gives you the plan, the story and the activity in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 32-week plan.' },
    { title: 'PREPARE', text: 'Read the page and the Bible passage in 10 minutes.' },
    { title: 'TEACH', text: 'Pray, tell the story, talk and do the activity.' },
    { title: 'SEND HOME', text: 'Closing prayer, take-home verse and parent note.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE SESSION',
  features: [
    { icon: 'goal', title: 'One clear session goal', text: 'One sentence of what the kids will know and do, the week to start it and what they need first.' },
    { icon: 'timer', title: 'A 60-minute session block', text: 'Welcome & prayer 8 · Bible story 12 · Faith talk 10 · Activity 15 · Worksheet 10 · Closing prayer 5.' },
    { icon: 'steps', title: 'The Bible story in kids’ words', text: '5 story steps with the passage to read, a key truth and 3 faith talk questions.' },
    { icon: 'questions', title: 'Ages 7–8 and 9–10', text: 'How to teach the same session to each age, plus a tricky question kids really ask.' },
    { icon: 'quote', title: 'Your words and prayers', text: 'Opening words and the opening and closing prayers, word for word, plus a pro tip.' },
    { icon: 'action', title: 'Activity, worksheet + parent note', text: 'A 15-minute activity with a room diagram, a printable worksheet and a note for families.' },
  ],
} as const;

// ── 5b. FAITH WORDS + AGE LEVELS ──────────────────
export const valuesBand = {
  eyebrow: 'ONE FAITH WORD FOR EVERY SESSION',
  title: 'TEN FAITH WORDS THE KIDS TAKE HOME.',
  sub: 'Every session has a faith word of the week and a take-home verse, so families pray and talk about it at home too.',
  itemLabel: 'SESSION',
  values: [
    { n: '01', value: 'Family' },
    { n: '02', value: 'Creation' },
    { n: '03', value: 'Yes' },
    { n: '04', value: 'Saint' },
    { n: '05', value: 'Hope' },
    { n: '06', value: 'Emmanuel' },
    { n: '07', value: 'Prayer' },
    { n: '08', value: 'Love' },
    { n: '09', value: 'Mercy' },
    { n: '10', value: 'Eucharist' },
  ],
  agesTitle: 'EVERY SESSION ADAPTS TO THE KIDS IN FRONT OF YOU',
  ages: [
    { age: 'AGES 7–8', text: 'Picture cards, gestures, acting out the story and short, simple words.' },
    { age: 'AGES 9–10', text: 'Retelling the story in their own words, putting steps in order and real-life examples.' },
    { age: 'MIXED GROUPS', text: 'Every session works for both levels at once, with the older kids helping lead.' },
  ],
};

// ── 5c. FOR DREs (new section in this landing: the coordinator who buys for the whole team) ──
export const dre = {
  eyebrow: 'FOR DREs & CATECHIST COORDINATORS',
  title: 'ONE PLAN FOR YOUR WHOLE CATECHIST TEAM.',
  sub: 'Give every volunteer the same year plan and the same ready sessions, so new catechists start strong and every group stays on track.',
  points: [
    { title: 'The same plan for every group', text: 'One 32-week map from September to May, with Advent, Lent and Easter in their place.' },
    { title: 'New catechists ready on day one', text: 'Every session says what to say and do, word for word, in 60 minutes.' },
    { title: 'A team kit included', text: 'Bonus 05: a program calendar, 6 team meeting agendas and a new catechist welcome guide.' },
  ],
  note: 'This purchase is for your own use. For several catechists, each one gets their own copy.',
};

// ── 6. THE 32-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 32-WEEK YEAR PLAN',
  title: 'SEPTEMBER TO MAY, FOLLOWING THE LITURGICAL YEAR.',
  sub: '★ weeks start a new session. ◆ weeks are review & celebrate weeks. The weeks in between have a ready theme, Bible passage and 10-minute activity.',
  quarters: [
    {
      q: 'FALL',
      weeks: 'WEEKS 1–8',
      name: 'WELCOME TO GOD’S FAMILY',
      full: [
        { week: '1', label: 'START SESSION 01', title: 'We Are God’s Family' },
        { week: '4', label: 'START SESSION 02', title: 'God Made Everything Good' },
        { week: '6', label: 'START SESSION 03', title: 'Mary, the Mother of Jesus' },
        { week: '8', label: 'START SESSION 04', title: 'The Saints, Friends of God' },
      ],
      activities: '+ ready weeks like Baptism: My Welcome Into the Church and The Rosary: Praying With Mary.',
    },
    {
      q: 'ADVENT',
      weeks: 'WEEKS 9–16',
      name: 'GETTING READY FOR JESUS',
      full: [
        { week: '11', label: 'REVIEW & CELEBRATE', title: 'Christ the King', review: true },
        { week: '12', label: 'START SESSION 05', title: 'Advent: Getting Ready for Jesus' },
        { week: '15', label: 'START SESSION 06', title: 'Jesus, God’s Son, Comes to Us' },
      ],
      activities: '+ ready weeks like John the Baptist: Prepare the Way and The Wise Men Find Jesus.',
    },
    {
      q: 'WINTER',
      weeks: 'WEEKS 17–24',
      name: 'LEARNING TO FOLLOW JESUS',
      full: [
        { week: '17', label: 'START SESSION 07', title: 'Jesus Teaches Us to Pray: The Our Father' },
        { week: '20', label: 'START SESSION 08', title: 'The Ten Commandments: God’s Loving Rules' },
        { week: '23', label: 'START SESSION 09', title: 'Jesus Forgives: The Lost Sheep' },
        { week: '24', label: 'REVIEW & CELEBRATE', title: 'A Simple Stations of the Cross', review: true },
      ],
      activities: '+ ready weeks like The Great Commandment and Lent Begins: Ash Wednesday.',
    },
    {
      q: 'SPRING',
      weeks: 'WEEKS 25–32',
      name: 'EASTER & FIRST COMMUNION',
      full: [
        { week: '27', label: 'START SESSION 10', title: 'The Mass: Our Great Celebration' },
        { week: '32', label: 'CELEBRATE', title: 'First Communion & year-end celebration', review: true },
      ],
      activities: '+ ready weeks like Palm Sunday, Easter: Jesus Is Risen! and First Communion Practice.',
    },
  ],
  toolsTitle: 'PLUS YOUR TEACHER TOOLS',
  tools: ['When the session gets hard: 8 situations, what to say and do', 'Session planner + attendance: plan any session in 10 minutes', 'Field card with the prayers and the liturgical colors'],
};

// ── 7. THE 10 COMPLETE SESSIONS ─────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE SESSIONS',
  title: 'TEN FIRST COMMUNION SESSIONS. READY TO TEACH.',
  sub: 'Follow the 32-week plan, or start with the session your class needs now. Each one is 3 pages: session plan, tell the story, activity & prayer.',
  weekLabel: 'START WEEK',
  items: [
    { n: '01', week: '1', title: 'We Are God’s Family', goal: 'Baptism, the Sign of the Cross and belonging to the Church family.' },
    { n: '02', week: '4', title: 'God Made Everything Good', goal: 'The story of creation in order, and how we care for God’s world.' },
    { n: '03', week: '6', title: 'Mary, the Mother of Jesus', goal: 'The angel’s visit to Mary, and praying the Hail Mary together.' },
    { n: '04', week: '8', title: 'The Saints, Friends of God', goal: 'Four saints and how we can become friends of God too.' },
    { n: '05', week: '12', title: 'Advent: Getting Ready for Jesus', goal: 'Waiting with hope, and the Advent wreath: 4 candles for 4 Sundays.' },
    { n: '06', week: '15', title: 'Jesus, God’s Son, Comes to Us', goal: 'The Christmas story, and Jesus as Emmanuel, God with us.' },
    { n: '07', week: '17', title: 'Jesus Teaches Us to Pray: The Our Father', goal: 'Praying the Our Father and what we ask God in it.' },
    { n: '08', week: '20', title: 'The Ten Commandments: God’s Loving Rules', goal: 'Ten loving rules to love God and love others, in simple words.' },
    { n: '09', week: '23', title: 'Jesus Forgives: The Lost Sheep', goal: 'God’s mercy and the steps of Reconciliation, with joy, not fear.' },
    { n: '10', week: '27', title: 'The Mass: Our Great Celebration', goal: 'The Last Supper, the 4 parts of the Mass and Communion practice.' },
  ],
  cta: 'GET ALL 10 SESSIONS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 BOOK',
  title: 'EVERYTHING ELSE YOUR YEAR NEEDS.',
  sub: 'Games, Advent and Christmas, First Reconciliation, parent messages and a team kit for DREs, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '40 Catechism Games & Activities',
      hook: 'Never run out of activities again.',
      title: '40 Catechism Games & Activities — No Supplies Needed',
      text: '40 simple, faith-filled games and activities for ages 7 to 10 that need nothing but paper, pencils and your voice: icebreakers, Bible story review, learning prayers, the sacraments, the saints, kindness and calm closings.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: 'Advent & Christmas Session Kit',
      hook: 'Ready before the First Sunday of Advent.',
      title: 'Advent & Christmas Session Kit — 4 Ready Sessions + a Nativity Celebration',
      text: '4 complete sessions for Advent and Christmas, an Advent wreath prayer for each week, a simple Jesse tree activity, printable Advent calendar pages for families and a short Nativity celebration with roles for the kids.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'First Reconciliation Prep Guide',
      hook: 'Prepare them with joy, not fear.',
      title: 'First Reconciliation Prep Guide — Help Kids Get Ready for Their First Confession',
      text: '3 ready sessions, a kid-friendly examination of conscience, the Act of Contrition with a practice card, a walk-through of the steps with the priest, answers to the questions kids ask most and a letter for parents.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: 'Parent Communication Pack',
      hook: 'Know exactly what to send to families.',
      title: 'Parent Communication Pack — Welcome Letter, Mass Reminders & Ready-to-Send Messages',
      text: 'A welcome letter, class rules for kids, Sunday Mass reminders, messages for missed sessions and sacrament dates, a First Communion timeline for parents and kind scripts for talking with families.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: 'Catechist Team Kit for DREs',
      hook: 'Lead your whole catechist team.',
      title: 'Catechist Team Kit for DREs — Year Calendar, Team Meeting Agendas & New Catechist Guide',
      text: 'A September-to-May program calendar, 6 ready catechist team meeting agendas with a formation moment and a prayer, a welcome guide for new volunteers, attendance sheets and messages to recruit and thank your catechists.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT CLASS STARTS HERE',
  title: 'YOUR WHOLE YEAR OF RELIGION CLASSES. ALL IN ONE BOOK.',
  kicker: 'THE COMPLETE DIGITAL BOOK',
  name: 'Catechist Ready™',
  description: 'A 32-week year plan and 10 complete, ready-to-teach 60-minute First Communion sessions, plus five bonuses: games, an Advent & Christmas kit, a First Reconciliation guide, parent messages and a team kit for DREs.',
  mockupAlt: 'Everything included in Catechist Ready™',
  includedTitle: 'YOUR BOOK INCLUDES',
  included: [
    '32-week plan: September to May, following the liturgical year',
    '10 complete First Communion sessions, 3 pages each',
    'A timed 60-minute session block in every session',
    'The Bible story in kids’ words, key truth and faith talk',
    'Opening and closing prayers, word for word',
    'Activity, printable worksheet and parent note',
    'Teacher tools: hard moments, planner, field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Catechist Ready™ (32-week plan + 10 complete sessions)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · Less than $1 a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout (new copy for this landing) */
  cta: 'YES, I WANT MY YEAR READY — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. Use it with your school or parish program and follow your parish and diocese for sacrament dates, prayers and practices.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Teach your next sessions with it. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOUR NEXT CLASS.',
  sub: 'Built for Catholic school religion teachers, parish catechists and the DREs who lead them. A ready-to-teach session book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF book: a 32-week year plan from September to May (every week has the start of a session, a ready theme with its Bible passage and a 10-minute activity, or a review and celebrate week) and 10 complete 60-minute First Communion sessions of 3 pages each, plus teacher tools and 5 bonuses.',
    },
    {
      q: 'I teach religion at a Catholic school. Does it work for my class?',
      a: 'Yes. The plan follows the school year from September to May, and the sessions fit a religion class of children preparing for First Communion. Each session is a timed 60-minute block that you can teach in one class or split across two shorter periods.',
    },
    {
      q: 'Is it Catholic?',
      a: 'Yes. It is written to be faithful to the teaching of the Catholic Church, with the traditional prayers (Sign of the Cross, Our Father, Hail Mary, Glory Be) and the seasons of the liturgical year. It is not tied to any publisher or diocesan program.',
    },
    {
      q: 'Does it replace my school or parish program?',
      a: 'No. Use it with your program. It gives you the ready-to-teach hour: what to say, the story in kids’ words, the activity and the worksheet. Always follow your parish and diocese for sacrament dates, prayers and practices.',
    },
    {
      q: 'What ages is it for?',
      a: 'Children from about 7 to 10, preparing for First Reconciliation and First Communion. Every session has an age level card for ages 7–8 and 9–10.',
    },
    {
      q: 'I have no teaching experience. Can I use it?',
      a: 'Yes. It was made so that anyone can teach it, including volunteer catechists. Every session tells you what to say, word for word, what to do minute by minute and what to send to the parents.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I use the sessions individually?',
      a: 'Yes. Each session stands alone, so you can start with the one your class needs now, or follow the 32-week plan from week 1.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet, and the worksheets and the session planner are ready to print.',
    },
    {
      q: 'I’m a DRE. Can I share it with my catechists?',
      a: 'This purchase is for your own use. Bonus 05 helps you lead and organize your team. For several catechists, each one gets their own copy.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO I TEACH TOMORROW?”',
  title: 'WALK INTO YOUR NEXT CLASS WITH THE SESSION READY.',
  text: 'Open this week’s page. Prepare in 10 minutes. Teach a session your kids will remember. Advent starts November 29: have it ready before then.',
  microcopy: '32-week plan + 10 complete sessions + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'CATECHIST READY™ · Session material for religion teachers and parish catechists. Use it with your program and follow your parish and diocese.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
